/**
 * FlyPage.js
 *
 * /fly/:pnr — a guest's invite to Flight YJ030 (Yash's 26th birthday):
 * their boarding pass, "Check-in now open", the invitation, and a Check-in button.
 *
 * The backend decides what the visitor may see. Signing in with the passenger's name claims
 * the pass, and from then on only that account sees the seat and origin. A browser that
 * checked in online (/fly/check-in, PNR + last name) sees them too, without signing in.
 */

import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { PlaneTakeoff } from "lucide-react";
import toast from "react-hot-toast";
import { useClientAuth } from "../../hooks/clientAuth";
import SignUpAndInModal from "../../components/SignUp/SignUpAndInModal";
import BoardingPass, { usePassLayout } from "./components/BoardingPass";
import Invitation from "./components/Invitation";
import PassengerNotice from "./components/PassengerNotice";
import RsvpCard from "./components/RsvpCard";
import HostCard from "./components/HostCard";
import BookingNotFound from "./components/BookingNotFound";
import HomeButton from "./components/HomeButton";
import { useShareablePass } from "./components/ShareablePass";
import { CHECK_IN_CLOSES, claimBoardingPass, getBoardingPass, PNR_PATTERN } from "./helpers/flyApi";
import { flyPath, isTrial, useNoIndex } from "./helpers/flyPage";
import CheckInCountdown from "./components/CheckInCountdown";

/**
 * Right under the pass: online check-in is open (until 20:00 on the day), or closed
 * @param {String} accent   -   The guest's accent colour
 */
const CheckInHeading = ({ accent }) => {
    // The trial run's check-in never closes
    const open = isTrial() || Date.now() < CHECK_IN_CLOSES.getTime();
    return (
        <div className="flex flex-col items-center gap-1 text-center px-2">
            <h2 className="text-xl sm:text-2xl font-bold tracking-[0.16em] uppercase" style={{ color: accent }}>
                {open ? "Check-in now open" : "Online check-in closed"}
            </h2>
            {open && <CheckInCountdown accent={accent} className="my-1" />}
            <p className="text-sm" style={{ color: "#5B6577" }}>
                {open
                    ? "Confirm you're coming, then check in online by 3:00 PM on 30 October with your PNR and last name."
                    : "You can still check in to see your boarding pass and add it to your Wallet."}
            </p>
        </div>
    );
};

/**
 * Takes the guest to online check-in, with their PNR filled in
 * @param {String} pnr      -   e.g. 'CF38US'
 * @param {String} accent   -   The guest's accent colour
 */
const CheckInButton = ({ pnr, accent }) => (
    <Link
        to={flyPath(`/fly/check-in?pnr=${encodeURIComponent(pnr)}`)}
        className="mx-auto inline-flex items-center gap-2 rounded-full px-8 py-3 text-sm font-bold tracking-[0.2em] select-none transition hover:opacity-90"
        style={{ background: accent, color: "#FBF7EF" }}
    >
        <PlaneTakeoff size={16} aria-hidden="true" /> CHECK-IN
    </Link>
);

const FlyPage = () => {
    const { pnr: pnrParam = "" } = useParams();
    const navigate = useNavigate();
    const { user, loading: authLoading, signOutUser } = useClientAuth();
    const [showSignIn, setShowSignIn] = useState(false);
    // { state: 'loading' | 'found' | 'notFound' | 'error', booking?, error? }
    const [result, setResult] = useState({ state: "loading" });
    const [reloadKey, setReloadKey] = useState(0);
    const passLayout = usePassLayout();
    const shareablePass = useShareablePass(result.booking?.pass, passLayout.mobile);

    useNoIndex();

    const pnr = pnrParam.trim().toUpperCase();
    const uid = user?.uid;

    // Lowercase links work too, but show the PNR in capitals
    useEffect(() => {
        if (pnrParam !== pnr) navigate(flyPath(`/fly/${pnr}`), { replace: true });
    }, [pnrParam, pnr, navigate]);

    // Load the pass whenever the PNR or the signed-in user changes, and claim it if the visitor's name matches
    useEffect(() => {
        if (authLoading) return;
        if (!PNR_PATTERN.test(pnr)) {
            setResult({ state: "notFound" });
            return;
        }

        let ignore = false;
        // Never keep showing one account's private fields after switching accounts
        setResult({ state: "loading" });

        const load = async () => {
            try {
                let booking = await getBoardingPass(pnr);
                if (booking.status === "claimable") booking = await claimBoardingPass(pnr);
                if (!ignore) setResult({ state: "found", booking });
            } catch (err) {
                if (ignore) return;
                if (err.status === 404) setResult({ state: "notFound" });
                else setResult({ state: "error", error: err.message || "Something went wrong" });
            }
        };
        load();
        return () => { ignore = true; };
    }, [pnr, authLoading, uid, reloadKey]);

    const reload = () => setReloadKey((key) => key + 1);

    const handleSignOut = async () => {
        try {
            await signOutUser();
            toast.success("Signed out.");
        } catch (err) {
            toast.error("Couldn't sign out.");
        }
    };

    return (
        <div className="min-h-screen w-full pb-16" style={{ background: "#E9E4DA", color: "#1B2A41", fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>
            {result.state === "loading" && (
                <p className="pt-32 text-center text-sm font-bold tracking-[0.24em] animate-pulse" style={{ color: "#5B6577" }}>
                    CHECKING YOUR BOOKING…
                </p>
            )}

            {result.state === "notFound" && <div className="px-2 sm:px-4 pt-16"><BookingNotFound pnr={pnr} /></div>}

            {result.state === "error" && (
                <div className="pt-32 px-4 flex flex-col items-center gap-4 text-center">
                    <p className="font-bold">We couldn't load your boarding pass.</p>
                    <p className="text-sm" style={{ color: "#5B6577" }}>{result.error}</p>
                    <button type="button" onClick={reload} className="text-xs font-bold tracking-[0.18em] underline underline-offset-4">TRY AGAIN</button>
                    <HomeButton />
                </div>
            )}

            {result.state === "found" && (
                <div className="flex flex-col gap-4 sm:gap-6 sm:pt-4">
                    <BoardingPass pass={result.booking.pass} layout={passLayout} />
                    {/* Same width as the ticket, at every screen size */}
                    <div className="mx-auto flex flex-col gap-4 sm:gap-6" style={{ width: passLayout.passWidth }}>
                        <CheckInHeading accent={result.booking.pass.colour} />
                        <PassengerNotice booking={result.booking} onSignIn={() => setShowSignIn(true)} onReload={reload} />
                        {result.booking.status === "owner" && result.booking.isAdmin && <HostCard booking={result.booking} />}
                        {(result.booking.status === "checked_in" || (result.booking.status === "owner" && !result.booking.isAdmin)) && (
                            <RsvpCard booking={result.booking} onChange={(booking) => setResult({ state: "found", booking })} />
                        )}
                        <Invitation accent={result.booking.pass.colour} actions={{ "shareable-pass": shareablePass.download }} />
                        <CheckInButton pnr={result.booking.pass.pnr} accent={result.booking.pass.colour} />
                        <HomeButton />
                        {user && (
                            <p className="text-center text-xs" style={{ color: "#5B6577" }}>
                                Signed in as {user.email || user.displayName || "you"} ·{" "}
                                <button type="button" onClick={handleSignOut} className="underline underline-offset-2">Sign out</button>
                            </p>
                        )}
                    </div>
                </div>
            )}

            {shareablePass.drawing}
            <SignUpAndInModal isOpen={showSignIn} onClose={() => setShowSignIn(false)} />
        </div>
    );
};

export default FlyPage;
